import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faCircleCheck, faTriangleExclamation, faPencil, faRuler, faWeight } from '@fortawesome/free-solid-svg-icons';
import { auth } from '../firebase';
import { updateProfile } from 'firebase/auth';
import { userService } from '../services/userService';

const UserModal = ({ isOpen, onClose, user, entries = [], onProfileUpdate }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.displayName || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  
  // Profile data states
  const [profile, setProfile] = useState({
    height: '',
    heightUnit: 'cm',
    sex: '',
    weight: '' // This will be populated from latest timeline entry
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Load user profile data
  useEffect(() => {
    if (isOpen && user?.uid) {
      loadUserProfile();
    }
  }, [isOpen, user?.uid]);

  // Calculate latest weight from entries
  useEffect(() => {
    if (entries && entries.length > 0) {
      const weightEntries = entries
        .filter(entry => entry.type === 'weight')
        .sort((a, b) => new Date(b.datetime) - new Date(a.datetime));
      
      if (weightEntries.length > 0) {
        setProfile(prev => ({
          ...prev,
          weight: weightEntries[0].value
        }));
      }
    }
  }, [entries]);

  const loadUserProfile = async () => {
    try {
      const userProfile = await userService.getUserProfile(user.uid);
      if (userProfile.profile) {
        setProfile(prev => ({
          ...prev,
          height: userProfile.profile.height || '',
          heightUnit: userProfile.profile.heightUnit || 'cm',
          sex: userProfile.profile.sex || ''
        }));
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const calculateBMI = () => {
    if (!profile.height || !profile.weight) return null;
    
    const weight = parseFloat(profile.weight);
    let heightInMeters;
    
    if (profile.heightUnit === 'cm') {
      heightInMeters = parseFloat(profile.height) / 100;
    } else {
      // Convert feet and inches to meters
      const [feet, inches] = profile.height.split("'").map(val => parseFloat(val.replace('"', '')) || 0);
      heightInMeters = (feet * 12 + inches) * 0.0254;
    }
    
    if (heightInMeters <= 0 || weight <= 0) return null;
    
    return weight / (heightInMeters * heightInMeters);
  };

  const getBMICategory = (bmi) => {
    if (!bmi) return '';
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal weight';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  };

  const getBMICategoryColor = (bmi) => {
    if (!bmi) return 'text-gray-500';
    if (bmi < 18.5) return 'text-blue-600';
    if (bmi < 25) return 'text-green-600';
    if (bmi < 30) return 'text-orange-600';
    return 'text-red-600';
  };

  const convertHeight = (height, fromUnit) => {
    if (!height) return '';
    
    if (fromUnit === 'cm') {
      // Convert cm to feet'inches"
      const totalInches = parseFloat(height) / 2.54;
      const feet = Math.floor(totalInches / 12);
      const inches = Math.round(totalInches % 12);
      return `${feet}'${inches}"`;
    } else {
      // Convert feet'inches" to cm
      const [feet, inches] = height.split("'").map(val => parseFloat(val.replace('"', '')) || 0);
      const totalCm = Math.round((feet * 12 + inches) * 2.54);
      return totalCm.toString();
    }
  };

  if (!isOpen) return null;

  const handleNameUpdate = async () => {
    if (!newName.trim()) return;
    
    setIsUpdating(true);
    setUpdateError('');

    try {
      await updateProfile(auth.currentUser, {
        displayName: newName.trim()
      });
      setIsEditingName(false);
    } catch (error) {
      setUpdateError('Failed to update name. Please try again.');
      console.error('Error updating name:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleProfileUpdate = async () => {
    if (!profile.height || !profile.sex) {
      setUpdateError('Please fill in height and sex to calculate BMI.');
      return;
    }

    setIsSavingProfile(true);
    setUpdateError('');

    try {
      await userService.updateUserProfile(user.uid, {
        height: profile.height,
        heightUnit: profile.heightUnit,
        sex: profile.sex
      });
      setIsEditingProfile(false);
      // Notify parent component that profile was updated
      if (onProfileUpdate) {
        onProfileUpdate();
      }
    } catch (error) {
      setUpdateError('Failed to update profile. Please try again.');
      console.error('Error updating profile:', error);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const bmi = calculateBMI();
  const bmiCategory = getBMICategory(bmi);
  const bmiCategoryColor = getBMICategoryColor(bmi);

  const missingFields = [];
  if (!profile.height) missingFields.push('height');
  if (!profile.sex) missingFields.push('sex');
  if (!profile.weight) missingFields.push('weight (add a weight entry to your timeline)');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-2xl shadow-xl max-w-lg w-full m-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">Your Profile</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          {/* Profile Section */}
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-teal-600 to-teal-300 w-12 h-12 rounded-full flex items-center justify-center">
              <span className="text-white text-base font-bold">
                {user.displayName ? user.displayName[0].toUpperCase() : user.email[0].toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="text-lg font-semibold text-gray-800 border-b border-gray-300 focus:border-teal-500 focus:outline-none pb-1"
                    placeholder="Enter your name"
                    autoFocus
                  />
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleNameUpdate}
                      disabled={isUpdating}
                      className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                    >
                      {isUpdating ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setIsEditingName(false);
                        setNewName(user.displayName || '');
                        setUpdateError('');
                      }}
                      className="text-gray-500 hover:text-gray-600 text-sm font-medium"
                      disabled={isUpdating}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <h4 className="text-lg font-semibold text-gray-800">
                    {user.displayName || 'Add your name'}
                  </h4>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPencil} className="w-3 h-3" />
                  </button>
                </div>
              )}
              {updateError && (
                <p className="text-red-500 text-sm mt-1">{updateError}</p>
              )}
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <FontAwesomeIcon icon={faEnvelope} className="w-4 h-4" />
                <span>{user.email}</span>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className={`p-4 rounded-lg ${user.emailVerified ? 'bg-green-50' : 'bg-yellow-50'}`}>
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon 
                icon={user.emailVerified ? faCircleCheck : faTriangleExclamation} 
                className={`w-5 h-5 ${user.emailVerified ? 'text-green-500' : 'text-yellow-500'}`}
              />
              <span className={`text-sm ${user.emailVerified ? 'text-green-700' : 'text-yellow-700'}`}>
                {user.emailVerified ? 'Email verified' : 'Email not verified'}
              </span>
            </div>
          </div>

          {/* BMI Card */}
          {bmi && (
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-xl border border-teal-100">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-lg font-semibold text-gray-800">Your BMI</h4>
                <div className="text-right">
                  <div className="text-2xl font-bold text-teal-600">{bmi.toFixed(1)}</div>
                  <div className={`text-sm font-medium ${bmiCategoryColor}`}>{bmiCategory}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                <div>
                  <FontAwesomeIcon icon={faRuler} className="w-4 h-4 mr-1" />
                  Height: {profile.height} {profile.heightUnit}
                  {profile.heightUnit === 'cm' && (
                    <div className="text-xs text-gray-500">{convertHeight(profile.height, 'cm')}</div>
                  )}
                  {profile.heightUnit === "ft'in\"" && (
                    <div className="text-xs text-gray-500">{convertHeight(profile.height, "ft'in\"")} cm</div>
                  )}
                </div>
                <div>
                  <FontAwesomeIcon icon={faWeight} className="w-4 h-4 mr-1" />
                  Weight: {profile.weight} kg
                  <div className="text-xs text-gray-500">{(parseFloat(profile.weight) * 2.20462).toFixed(1)} lbs</div>
                </div>
                <div>
                  Sex: {profile.sex}
                </div>
              </div>
            </div>
          )}

          {/* Profile Details Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Health Profile</h4>
              <button
                onClick={() => setIsEditingProfile(!isEditingProfile)}
                className="text-teal-600 hover:text-teal-700 text-sm font-medium"
              >
                {isEditingProfile ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {isEditingProfile ? (
              <div className="space-y-4">
                {/* Height Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={profile.height}
                      onChange={(e) => setProfile(prev => ({ ...prev, height: e.target.value }))}
                      className="flex-1 p-2 border rounded-lg focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                      placeholder={profile.heightUnit === 'cm' ? 'e.g. 170' : "e.g. 5'8\""}
                    />
                    <select
                      value={profile.heightUnit}
                      onChange={(e) => {
                        const newUnit = e.target.value;
                        setProfile(prev => ({
                          ...prev,
                          heightUnit: newUnit,
                          height: convertHeight(prev.height, prev.heightUnit)
                        }));
                      }}
                      className="p-2 border rounded-lg focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                    >
                      <option value="cm">cm</option>
                      <option value="ft">ft'in"</option>
                    </select>
                  </div>
                </div>

                {/* Sex Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sex</label>
                  <select
                    value={profile.sex}
                    onChange={(e) => setProfile(prev => ({ ...prev, sex: e.target.value }))}
                    className="w-full p-2 border rounded-lg focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
                  >
                    <option value="">Select sex</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                {/* Weight Display (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current Weight</label>
                  <div className="p-2 bg-gray-100 rounded-lg text-gray-600">
                    {profile.weight ? `${profile.weight} kg` : 'No weight entries found in timeline'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Weight is automatically taken from your latest timeline entry
                  </p>
                </div>

                <button
                  onClick={handleProfileUpdate}
                  disabled={isSavingProfile}
                  className="w-full py-2 px-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  {isSavingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Height:</span>
                    <div className="font-medium">{profile.height ? `${profile.height} ${profile.heightUnit}` : 'Not set'}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Sex:</span>
                    <div className="font-medium">{profile.sex || 'Not set'}</div>
                  </div>
                </div>
                <div>
                  <span className="text-gray-600 text-sm">Current Weight:</span>
                  <div className="font-medium">{profile.weight ? `${profile.weight} kg` : 'No weight entries'}</div>
                </div>
              </div>
            )}
          </div>

          {/* BMI Missing Fields Notice */}
          {!bmi && missingFields.length > 0 && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>To calculate your BMI:</strong> Please enter your {missingFields.join(', ')}.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserModal;
